import { Injectable } from '@angular/core'
import { addDoc, collection, collectionData, doc, docData, Firestore, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from '@angular/fire/firestore'
import { AdminClient, FirestoreStructure, LeadClosedReason, LeadStatus } from '@parkupp/core'

// Contact type enum - matches @parkupp/core ContactType
export enum ContactType {
    USER = 'User',
    LEAD = 'Lead',
    LANDLORD = 'Landlord',
}
import { BehaviorSubject, map, Observable, Subject, Subscription } from 'rxjs'
import { DocumentData } from 'rxfire/firestore/interfaces'
import * as firestore from 'firebase/firestore'
import { Auth } from '@angular/fire/auth'
import { AuthenticationService } from './authentication.service'
import { Router } from '@angular/router'
import { GlobalService } from './global.service'
import { CampaignTrackingService } from './campaign-tracking.service'

@Injectable({
    providedIn: 'root',
})
export class AdminClientService {
    collection = collection(this.firestore, FirestoreStructure.ADMIN_CLIENTS)

    activeAdminClient: AdminClient | null
    adminClientSubscription: Subscription
    activeAdminClientReady = new Subject()

    private activeAdminClientSubject = new BehaviorSubject<AdminClient | null>(null)
    activeAdminClient$: Observable<AdminClient | null> = this.activeAdminClientSubject.asObservable()

    constructor(
        public firestore: Firestore,
        public auth: Auth,
        private authenticationSvc: AuthenticationService,
        private router: Router,
        private globalService: GlobalService,
        private campaignTrackingService: CampaignTrackingService
    ) {
        this.auth.onAuthStateChanged((user) => {
            this.activeAdminClient = null
            // Only try to get admin client for non-anonymous authenticated users
            if (user?.uid && !user.isAnonymous) {
                this.getActiveAdminClient(user?.uid!)
            }
        })
    }

    getDocRef(adminClientKey: string) {
        return doc(this.firestore, `${FirestoreStructure.ADMIN_CLIENTS}/${adminClientKey}`)
    }

    list(): Observable<AdminClient[]> {
        const adminClientsQuery = query(this.collection)
        return collectionData(adminClientsQuery, { idField: '$key' }) as Observable<AdminClient[]>
    }

    get(adminClientId: string): Observable<any> {
        const adminClientDocRef = doc(this.firestore, `${FirestoreStructure.ADMIN_CLIENTS}/${adminClientId}`)
        return docData(adminClientDocRef, { idField: '$key' })
    }

    async getActiveAdminClient(uid: string) {
        if (this.adminClientSubscription) {
            this.adminClientSubscription.unsubscribe()
        }
        const documentRef = doc(this.firestore, FirestoreStructure.ADMIN_CLIENTS, uid)
        const documentSnap = await getDoc(documentRef)

        if (documentSnap.exists()) {
            this.activeAdminClient = documentSnap.data() as AdminClient
            this.activeAdminClient.$key = uid

            this.activeAdminClientReady.next(this.activeAdminClient! as AdminClient)
            this.activeAdminClientSubject.next(this.activeAdminClient! as AdminClient)
        }
    }

    async getOnce(uid: string): Promise<AdminClient | null> {
        const documentRef = doc(this.firestore, FirestoreStructure.ADMIN_CLIENTS, uid)
        const documentSnap = await getDoc(documentRef)

        if (documentSnap.exists()) {
            const adminClient = documentSnap.data() as AdminClient
            adminClient.$key = uid
            return adminClient
        }
        return null
    }

    createAdminClient(adminClient: AdminClient, uid: string): Promise<any> {
        adminClient.createdAt = serverTimestamp() as firestore.Timestamp
        adminClient.updatedAt = serverTimestamp() as firestore.Timestamp
        const reference = doc(this.firestore, FirestoreStructure.ADMIN_CLIENTS, uid)
        return setDoc(reference, adminClient.parse())
    }

    update(adminClient: AdminClient): Promise<any> {
        const reference = doc(this.firestore, FirestoreStructure.ADMIN_CLIENTS, adminClient.$key!)
        
        // Build clean update object to avoid undefined values
        const updateData: Record<string, any> = {
            updatedAt: serverTimestamp(),
        }

        // Only include fields that have actual values
        const fieldsToUpdate = [
            'firstName', 'lastName', 'companyName', 'email', 'phoneNumber',
            'numberOfBays', 'monthsRequired', 'broadLocation', 'city',
            'fullAddress', 'latitude', 'longitude',
            'userResponsible', 'leadStatus', 'leadClosedReason', 'contactType',
            'p_source', 'p_campaign', 'comment', 'searchableField',
            'hasActiveSubscription', 'hasFutureSubscription', 'hasEverHadActiveOrCompletedSubscription',
            'breachedSubscriptionsCount', 'completedSubscriptionsCount',
            'noAuth', 'emailVerified', 'authTypes', 'isManualLead', 'isStarred',
            'commentsCount', 'latestComment', 'billingAddress', 'vatNumber',
            'omnisendContactKey', 'omnisendCartKey', 'weWorkId', 'profileImage',
            'nonRenewalReason', 'tags'
        ]

        fieldsToUpdate.forEach(field => {
            const value = (adminClient as any)[field]
            if (value !== undefined) {
                updateData[field] = value
            }
        })

        // Handle timestamp fields separately - only include if they exist and are not undefined
        if (adminClient.createdAt !== undefined && adminClient.createdAt !== null) {
            updateData['createdAt'] = adminClient.createdAt
        }
        if (adminClient.authUserCreatedAt !== undefined && adminClient.authUserCreatedAt !== null) {
            updateData['authUserCreatedAt'] = adminClient.authUserCreatedAt
        }
        if (adminClient.clientProfileCreatedAt !== undefined && adminClient.clientProfileCreatedAt !== null) {
            updateData['clientProfileCreatedAt'] = adminClient.clientProfileCreatedAt
        }
        if (adminClient.activeSubscriptionExpiry !== undefined && adminClient.activeSubscriptionExpiry !== null) {
            updateData['activeSubscriptionExpiry'] = adminClient.activeSubscriptionExpiry
        }

        return updateDoc(reference, updateData)
    }

    /**
     * Create a new manual lead with a random ID
     * Builds clean data object to avoid undefined values
     */
    async createManualLead(adminClient: AdminClient, enterpriseKey?: string): Promise<string> {
        // Build clean data object manually to avoid any undefined values
        const data: Record<string, any> = {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            isManualLead: true,
            contactType: adminClient.contactType || ContactType.LEAD, // Default to Lead for manual entries
            // Enterprise source - use provided key, or fall back to current context, or default to 'parkupp'
            denormEnterpriseKey: enterpriseKey || this.globalService.enterpriseKey || 'parkupp',
        }

        // Only add fields that have actual values
        if (adminClient.firstName) data['firstName'] = adminClient.firstName
        if (adminClient.lastName) data['lastName'] = adminClient.lastName
        if (adminClient.companyName) data['companyName'] = adminClient.companyName
        if (adminClient.email) data['email'] = adminClient.email
        if (adminClient.phoneNumber) data['phoneNumber'] = adminClient.phoneNumber
        if (adminClient.numberOfBays !== undefined && adminClient.numberOfBays !== null) data['numberOfBays'] = adminClient.numberOfBays
        if (adminClient.monthsRequired !== undefined && adminClient.monthsRequired !== null) data['monthsRequired'] = adminClient.monthsRequired
        if (adminClient.broadLocation) data['broadLocation'] = adminClient.broadLocation
        if (adminClient.userResponsible) data['userResponsible'] = adminClient.userResponsible
        if (adminClient.p_source) data['p_source'] = adminClient.p_source
        if (adminClient.leadStatus) data['leadStatus'] = adminClient.leadStatus
        if (adminClient.searchableField) data['searchableField'] = adminClient.searchableField
        
        // Boolean fields with defaults
        data['hasActiveSubscription'] = adminClient.hasActiveSubscription ?? false
        data['hasFutureSubscription'] = adminClient.hasFutureSubscription ?? false
        data['hasEverHadActiveOrCompletedSubscription'] = adminClient.hasEverHadActiveOrCompletedSubscription ?? false
        data['breachedSubscriptionsCount'] = adminClient.breachedSubscriptionsCount ?? 0
        data['completedSubscriptionsCount'] = adminClient.completedSubscriptionsCount ?? 0
        data['noAuth'] = adminClient.noAuth ?? true
        data['emailVerified'] = adminClient.emailVerified ?? false
        data['authTypes'] = adminClient.authTypes ?? []

        const docRef = await addDoc(this.collection, data)
        return docRef.id
    }

    /**
     * Create a lead from a website form submission (not marked as manually added)
     */
    /**
     * Create a lead from a website form submission (not marked as manually added)
     * Includes initial comment text to avoid needing to update metadata later
     */
    async createFormLead(adminClient: AdminClient, initialCommentText?: string, enterpriseKey?: string): Promise<string> {
        const data: Record<string, any> = {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            contactType: adminClient.contactType || ContactType.LEAD,
            // Enterprise source - use provided key, or fall back to current context, or default to 'parkupp'
            denormEnterpriseKey: enterpriseKey || this.globalService.enterpriseKey || 'parkupp',
        }

        // Only add fields that have actual values
        if (adminClient.firstName) data['firstName'] = adminClient.firstName
        if (adminClient.lastName) data['lastName'] = adminClient.lastName
        if (adminClient.companyName) data['companyName'] = adminClient.companyName
        if (adminClient.email) data['email'] = adminClient.email
        if (adminClient.phoneNumber) data['phoneNumber'] = adminClient.phoneNumber
        if (adminClient.numberOfBays !== undefined && adminClient.numberOfBays !== null) data['numberOfBays'] = adminClient.numberOfBays
        if (adminClient.monthsRequired !== undefined && adminClient.monthsRequired !== null) data['monthsRequired'] = adminClient.monthsRequired
        if (adminClient.broadLocation) data['broadLocation'] = adminClient.broadLocation
        if (adminClient.city) data['city'] = adminClient.city
        if (adminClient.fullAddress) data['fullAddress'] = adminClient.fullAddress
        if (adminClient.latitude !== undefined && adminClient.latitude !== null) data['latitude'] = adminClient.latitude
        if (adminClient.longitude !== undefined && adminClient.longitude !== null) data['longitude'] = adminClient.longitude
        if (adminClient.userResponsible) data['userResponsible'] = adminClient.userResponsible
        if (adminClient.p_source) data['p_source'] = adminClient.p_source
        if (adminClient.leadStatus) data['leadStatus'] = adminClient.leadStatus
        if (adminClient.searchableField) data['searchableField'] = adminClient.searchableField
        
        // Boolean fields with defaults
        data['hasActiveSubscription'] = adminClient.hasActiveSubscription ?? false
        data['hasFutureSubscription'] = adminClient.hasFutureSubscription ?? false
        data['hasEverHadActiveOrCompletedSubscription'] = adminClient.hasEverHadActiveOrCompletedSubscription ?? false
        data['breachedSubscriptionsCount'] = adminClient.breachedSubscriptionsCount ?? 0
        data['completedSubscriptionsCount'] = adminClient.completedSubscriptionsCount ?? 0
        data['noAuth'] = adminClient.noAuth ?? true
        data['emailVerified'] = adminClient.emailVerified ?? false
        data['authTypes'] = adminClient.authTypes ?? []

        // Set initial comment metadata if provided (so we don't need to update later)
        if (initialCommentText) {
            data['commentsCount'] = 1
            data['latestComment'] = {
                text: initialCommentText,
                createdBy: 'system@parkupp.co.za',
                createdByName: 'System',
                createdAt: serverTimestamp(),
            }
        }

        // Only this public form entry point reads visitor attribution. Admin-created CRM
        // clients must never inherit the operator's browser cookies.
        const campaign = this.campaignTrackingService.getCampaignData()
        for (const field of ['p_source', 'p_campaign', 'gclid', 'gbraid', 'wbraid'] as const) {
            if (campaign[field]) data[field] = campaign[field]
        }
        if (campaign.googleClickCapturedAt) {
            data['googleClickCapturedAt'] = firestore.Timestamp.fromDate(new Date(campaign.googleClickCapturedAt))
        }
        const docRef = await addDoc(this.collection, data)
        return docRef.id
    }

    async listClosedNoAvailabilityLeads(): Promise<AdminClient[]> {
        const q = query(this.collection, where('leadStatus', '==', LeadStatus.CLOSED))
        const snapshot = await getDocs(q)
        const results: AdminClient[] = []
        snapshot.forEach((d) => {
            const data = d.data() as AdminClient
            data.$key = d.id
            if (
                data.leadClosedReason === LeadClosedReason.NO_AVAILABILITY &&
                data.latitude !== undefined &&
                data.longitude !== undefined
            ) {
                results.push(data)
            }
        })
        return results
    }
}
